import React, { useRef } from 'react';
import Icon from '../Icon';
import '../../styles/components/RichTextEditor.css';

interface RichTextEditorProps {
  value: string;
  onChange: (value: string) => void;
  label?: string;
  error?: string;
  help?: string;
  required?: boolean;
  disabled?: boolean;
  className?: string;
  rows?: number;
  placeholder?: string;
  id?: string;
  onBlur?: () => void;
}

// Wraps the current selection (or inserts at the cursor) with markdown
// syntax, then restores focus/selection so the user can keep typing —
// mirrors how native rich-text toolbars behave without needing an actual
// WYSIWYG editor or an HTML-sanitization story.
function applyMarkdown(
  textarea: HTMLTextAreaElement,
  before: string,
  after: string = before,
  placeholder = ''
): string {
  const { selectionStart, selectionEnd, value } = textarea;
  const selected = value.slice(selectionStart, selectionEnd) || placeholder;
  const next = value.slice(0, selectionStart) + before + selected + after + value.slice(selectionEnd);
  return next;
}

function applyLinePrefix(textarea: HTMLTextAreaElement, prefix: string): string {
  const { selectionStart, selectionEnd, value } = textarea;
  const lineStart = value.lastIndexOf('\n', selectionStart - 1) + 1;
  let lineEnd = value.indexOf('\n', selectionEnd);
  if (lineEnd === -1) lineEnd = value.length;
  const block = value.slice(lineStart, lineEnd);
  const prefixed = block
    .split('\n')
    .map(line => (line.startsWith(prefix) ? line : `${prefix}${line}`))
    .join('\n');
  return value.slice(0, lineStart) + prefixed + value.slice(lineEnd);
}

/**
 * Markdown-formatted text field. Stores plain markdown in `value` (no
 * wrapper object) — a toolbar inserts markdown syntax at the cursor so
 * users don't need to know the syntax themselves. Pair with <MarkdownText>
 * wherever the value is displayed.
 */
export default function RichTextEditor({
  value,
  onChange,
  label,
  error,
  help,
  required,
  disabled,
  className = '',
  rows = 4,
  placeholder,
  id,
  onBlur,
}: RichTextEditorProps) {
  const textareaRef = useRef<HTMLTextAreaElement>(null);
  const fieldId = id ?? `rte-${Math.random().toString(36).substr(2, 9)}`;

  const runCommand = (fn: (ta: HTMLTextAreaElement) => string) => {
    const ta = textareaRef.current;
    if (!ta) return;
    onChange(fn(ta));
    // Restore focus after the state update re-renders the textarea.
    requestAnimationFrame(() => ta.focus());
  };

  return (
    <div className={`form-group rte-root ${className}`}>
      {label && (
        <label className="form-label" htmlFor={fieldId}>
          {label}
          {required && <span className="form-required">*</span>}
        </label>
      )}
      <div className={`rte-toolbar${disabled ? ' rte-toolbar--disabled' : ''}`}>
        <button type="button" className="rte-toolbar-btn rte-toolbar-btn--glyph" disabled={disabled} title="Bold"
          onMouseDown={e => e.preventDefault()}
          onClick={() => runCommand(ta => applyMarkdown(ta, '**', '**', 'bold text'))}>
          <strong>B</strong>
        </button>
        <button type="button" className="rte-toolbar-btn rte-toolbar-btn--glyph" disabled={disabled} title="Italic"
          onMouseDown={e => e.preventDefault()}
          onClick={() => runCommand(ta => applyMarkdown(ta, '_', '_', 'italic text'))}>
          <em>I</em>
        </button>
        <button type="button" className="rte-toolbar-btn" disabled={disabled} title="Bullet list"
          onMouseDown={e => e.preventDefault()}
          onClick={() => runCommand(ta => applyLinePrefix(ta, '- '))}>
          <Icon name="list" size={14} />
        </button>
        <button type="button" className="rte-toolbar-btn" disabled={disabled} title="Link"
          onMouseDown={e => e.preventDefault()}
          onClick={() => runCommand(ta => applyMarkdown(ta, '[', '](https://)', 'link text'))}>
          <Icon name="link" size={14} />
        </button>
      </div>
      <textarea
        ref={textareaRef}
        id={fieldId}
        value={value}
        onChange={e => onChange(e.target.value)}
        onBlur={onBlur}
        disabled={disabled}
        rows={rows}
        placeholder={placeholder}
        className={`form-input rte-textarea${error ? ' has-error' : ''}`}
      />
      {error && <span className="form-error-text">{error}</span>}
      {!error && help && <span className="form-helper">{help}</span>}
    </div>
  );
}
